import { useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MonthInput } from "@/components/DateInput";
import { useAsyncData } from "@/lib/useAsyncData";
import Pagination from "@/components/Pagination";
import { ReportError, ReportLoading } from "@/components/leasing/ReportState";
import { fmtNumber, fmtDate } from "@/lib/format";
import { Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

function currentYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ReportP1({ registru }) {
  const [ym, setYm] = useState(currentYm());
  const [tab, setTab] = useState("report");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const reportMonth = ym ? `${ym}-01` : "";
  const { loading, error, data, reload } = useAsyncData(async () => {
    if (!reportMonth) return null;
    const res = await api.get("/prepaid/report/p1", { params: { registru, report_month: reportMonth, search, page, page_size: pageSize } });
    return res.data;
  }, [registru, reportMonth, search, page]);

  const onSearch = (v) => { setSearch(v); setPage(1); };

  const doExport = async () => {
    try { await downloadFile(`/prepaid/report/p1/export?registru=${registru}&report_month=${reportMonth}`, `Raport_P1_${registru}.xlsx`); }
    catch (e) { toast.error("Export eșuat"); }
  };

  const cols = [
    ["insurer", "Asigurător", "l"], ["data_plata", "Data plată", "d"], ["nr_polita", "Nr poliță", "l"],
    ["valoare", "Valoare", "n"], ["moneda", "Mon.", "l"], ["curs", "Curs", "n"], ["value_ron", "Valoare RON", "n"],
    ["daily_value", "Val. zilnică", "n4"], ["repartizat", "Repartizat (VRT+VRL)", "n"], ["vrl", "Rep. lună (VRL)", "n"],
    ["vrt", "Rep. anterior (VRT)", "n"], ["vrv", "De repartizat (VRV)", "n"], ["returned_value", "Val. restituită", "n"],
    ["overpaid_difference", "Dif. achitată în plus", "n"], ["remaining_to_allocate", "Rest de repartizat", "n"],
    ["check", "Verif.", "chk"],
  ];

  const rows = data?.rows || [];
  const totals = data?.totals;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Lună raport (LL-AAAA)</Label>
          <MonthInput value={ym} onChange={(v) => { setYm(v); setPage(1); }} className="w-40" data-testid="p1-report-month" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Caută</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="p1-search-input" placeholder="Nr poliță, asigurător…" value={search} onChange={(e) => onSearch(e.target.value)} className="pl-8 w-64" />
          </div>
        </div>
        {loading && <span className="flex items-center gap-1 text-xs text-slate-400 pb-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> se calculează…</span>}
        <Button onClick={doExport} variant="outline" className="gap-2 ml-auto" data-testid="p1-export-btn"><Download className="h-4 w-4" /> Export Excel</Button>
      </div>

      {totals && !error && (
        <div className="flex flex-wrap gap-4 text-sm bg-slate-900 text-white rounded p-3" data-testid="p1-totals">
          <span>Repartizat (VRT+VRL): <b className="tabular text-[#FFC800]">{fmtNumber(totals.repartizat)}</b></span>
          <span>Repartizat lună (VRL): <b className="tabular">{fmtNumber(totals.vrl)}</b></span>
          <span>De repartizat (VRV): <b className="tabular">{fmtNumber(totals.vrv)}</b></span>
          <span>Total restituit: <b className="tabular">{fmtNumber(totals.returned)}</b></span>
          <span>Total dif. achitată în plus: <b className="tabular">{fmtNumber(totals.overpaid)}</b></span>
        </div>
      )}

      <div className="flex gap-1">
        <button onClick={() => setTab("report")} data-testid="p1-tab-report" className={`px-3 py-1.5 text-xs font-semibold rounded ${tab === "report" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Raport</button>
        <button onClick={() => setTab("notfound")} data-testid="p1-tab-notfound" className={`px-3 py-1.5 text-xs font-semibold rounded ${tab === "notfound" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          Polițe negăsite {data ? `(${data.not_found.length})` : ""}
        </button>
      </div>

      {error ? <ReportError onRetry={reload} /> : loading && !data ? <ReportLoading /> : tab === "report" ? (
        <>
        <div className="border rounded-md overflow-auto max-h-[60vh]">
          <table className="text-xs jh-table" data-testid="report-p1-table">
            <thead className="sticky top-0 bg-slate-100">
              <tr>{cols.map((c) => <th key={c[0]} className={`px-2 py-2 whitespace-nowrap ${c[2] === "l" || c[2] === "chk" ? "text-left" : "text-right"}`}>{c[1]}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-amber-50/40">
                  {cols.map((c) => {
                    const v = r[c[0]];
                    if (c[2] === "l") return <td key={c[0]} className="px-2 py-1.5 whitespace-nowrap">{v}</td>;
                    if (c[2] === "d") return <td key={c[0]} className="px-2 py-1.5 text-right tabular">{fmtDate(v)}</td>;
                    if (c[2] === "n4") return <td key={c[0]} className="px-2 py-1.5 text-right tabular">{fmtNumber(v, 4)}</td>;
                    if (c[2] === "chk") return <td key={c[0]} className={`px-2 py-1.5 font-semibold ${v === "Ok" ? "text-emerald-600" : "text-red-600"}`}>{v}</td>;
                    return <td key={c[0]} className="px-2 py-1.5 text-right tabular">{fmtNumber(v)}</td>;
                  })}
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-slate-400">Nicio poliță.</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={pageSize} total={data?.total || 0} onPage={setPage} label="polițe" testid="p1-pagination" />
        </>
      ) : (
        <div className="space-y-3">
          <div className="text-sm bg-slate-50 rounded p-3">Total restituit (negăsite): <b className="tabular">{fmtNumber(data?.not_found_total)}</b></div>
          <div className="border rounded-md overflow-auto max-h-[55vh]">
            <table className="w-full text-sm jh-table" data-testid="report-p1-notfound-table">
              <thead className="sticky top-0 bg-slate-100"><tr><th className="text-left px-3 py-2">Nr poliță</th><th className="text-right px-3 py-2">Valoare restituită</th><th className="text-left px-3 py-2">Data returnare</th></tr></thead>
              <tbody>
                {(data?.not_found || []).map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-1.5">{r.nr_polita}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtNumber(r.valoare_returnata)}</td>
                    <td className="px-3 py-1.5 tabular">{fmtDate(r.data_returnare)}</td>
                  </tr>
                ))}
                {(!data || data.not_found.length === 0) && <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">Nicio poliță negăsită.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
